import {
  FileCountBounds,
  MEDIA_ATTACHMENT_FIELD_DEFAULT_PROPS,
  MediaAttachmentFieldProps,
  resolveFileCountBounds,
} from '../core-field/media-attachment-field-props'

/**
 * One photo the field asks for, in capture order. Photos map to slots
 * positionally — the Nth photo satisfies the Nth option — so a slot's
 * guidance is whatever `guidedCaptureOptions[N]` says.
 */
export interface GuidedCaptureOption {
  /** Short name for this photo. Labels its placeholder tile and its guidance step. */
  label: string
  /** Instructions shown alongside the sample before the camera opens. */
  helpText?: string
  /**
   * Prompt on the review step, after the photo is taken. Falls back to generic
   * wording. Use it to name what this particular shot has to show — 'Check the
   * seal number is legible' catches a bad photo that 'check it's clear' won't.
   */
  reviewText?: string
  /** Reference photo shown in the guidance step, so the user knows what to frame. */
  sampleImageUri?: string
}

/**
 * Photo-capture field. Replaces the legacy `FormPhotoCaptureField`.
 *
 * Scanning a value out of a photo is `ui2:scanField`'s job, not this field's —
 * a form that needs both a photo and a scanned number configures both.
 */
export const UI2_PHOTO_CAPTURE_FIELD_DEFAULT_PROPS: Partial<UI2PhotoCaptureFieldProps> = {
  ...MEDIA_ATTACHMENT_FIELD_DEFAULT_PROPS,
  allowPhotoLibrary: false,
}

export interface UI2PhotoCaptureFieldProps extends MediaAttachmentFieldProps {
  /**
   * The photos this field asks for, in order — one entry per photo, each with
   * its own label, instructions and sample image.
   *
   * Setting this turns the field into a *guided* capture: tapping the add
   * affordance walks the user through every outstanding slot in turn —
   * sample and instructions, then the camera, then a review step — and moves
   * straight on to the next unfilled slot. Relaunching after a partial fill
   * picks up at the first slot that has no photo.
   *
   * The list length is the field's minimum count unless `minFiles` overrides
   * it, since asking for N named photos and requiring fewer is almost always
   * a mistake.
   *
   * Guided capture is implemented on MW today; NM and DW read the labels for
   * their placeholder tiles but still capture one photo at a time.
   */
  guidedCaptureOptions?: GuidedCaptureOption[]

  // ─── Shared Capture Options ─────────────────────────────────────────────────
  /** Auto-preprocess captured frames to grayscale. */
  enableAutoGrayscale?: boolean
  /**
   * Enable page-detection cropping. On mobile app, this will overlay a bounding box on
   * detected pages before the user captures the image. On mobile web, this will
   * automatically crop the detected page after capture. */
  enablePageDetection?: boolean
  /** Open the capture modal immediately on mount. */
  openCaptureOnMount?: boolean
  /**
   * Lets the user pick an existing photo from the device instead of taking a
   * new one. Off by default: this field exists to capture evidence at the
   * point of work, and a library picker is a way around that, so a schema
   * author has to ask for it deliberately.
   *
   * NM adds a 'Choose from library' action; MW shows a library button beside
   * the shutter. Ignored on DW, where the only picker IS the file picker.
   *
   * Note the MW camera fallback is unaffected — when the camera can't start at
   * all, MW still opens the file input regardless of this flag, because the
   * alternative is a field that can't be filled in.
   */
  allowPhotoLibrary?: boolean
}

// ─── Capture slots ──────────────────────────────────────────────────────────

export interface CaptureSlot {
  /** Absolute position in `guidedCaptureOptions`, and the index of the photo that fills it. */
  index: number
  option: GuidedCaptureOption
}

/**
 * Count bounds for a photo field. Identical to the media-attachment rule
 * except that `guidedCaptureOptions` sets the minimum when `minFiles` is
 * absent — a field naming three photos asks for three.
 */
export function resolvePhotoCaptureBounds(
  props: Pick<UI2PhotoCaptureFieldProps, 'minFiles' | 'maxFiles' | 'isRequired' | 'guidedCaptureOptions'>,
): FileCountBounds {
  const slotCount = props.guidedCaptureOptions?.length ?? 0
  if (props.minFiles == null && slotCount > 0) {
    return resolveFileCountBounds({ ...props, minFiles: slotCount })
  }
  return resolveFileCountBounds(props)
}

/** A photo field is required when it has named slots, or by the usual count rules. */
export function isPhotoCaptureRequired(
  props: Pick<UI2PhotoCaptureFieldProps, 'minFiles' | 'maxFiles' | 'isRequired' | 'guidedCaptureOptions'>,
): boolean {
  return !!props.isRequired || resolvePhotoCaptureBounds(props).min > 0
}

/**
 * The slots still owed, in capture order. Photos fill slots positionally, so
 * everything from `fileCount` onward is outstanding — which is also what makes
 * relaunching mid-way resume at the right step rather than starting over.
 */
export function resolveOutstandingCaptureSlots(
  options: GuidedCaptureOption[] | undefined,
  fileCount: number,
): CaptureSlot[] {
  if (!options?.length) return []
  return options
    .map((option, index) => ({ index, option }))
    .filter(({ index }) => index >= fileCount)
}
